import { useState, useEffect, useCallback } from 'react';
import { MeetingCard } from './MeetingCard';
import { type CalendarEvent, type KioskSettings } from '@/types/calendar';
import LogoIcon from '@/components/ui/logo';
import { fetchGoogleCalendarEvents, getSettings } from '@/lib/calendar';
import { Settings } from 'lucide-react';
import { format } from 'date-fns';
import { IconButton } from '@/components/ui/icon-button';

interface DashboardProps {
  onOpenSettings: () => void;
}

interface ZoomMeeting {
  uuid?: string;
  id?: number | string;
  topic?: string;
  agenda?: string;
  start_time?: string;
  duration?: number;
  timezone?: string;
  join_url?: string;
  host_email?: string;
  host_name?: string;
}

interface ZoomMeetingsResponse {
  meetings?: ZoomMeeting[];
  message?: string;
}

let sharedEncoder: TextEncoder | null = null;

const getEncoder = () => {
  if (!sharedEncoder) {
    if (typeof TextEncoder === 'undefined') {
      throw new Error('TextEncoder is not available in this environment.');
    }
    sharedEncoder = new TextEncoder();
  }
  return sharedEncoder;
};

const bytesToBase64 = (bytes: Uint8Array): string => {
  if (typeof btoa === 'function') {
    let binary = '';
    for (let i = 0; i < bytes.length; i += 1) {
      binary += String.fromCharCode(bytes[i] ?? 0);
    }
    return btoa(binary);
  }

  throw new Error('Base64 encoding is not supported in this environment.');
};

const base64UrlEncode = (input: string | ArrayBuffer): string => {
  const bytes = typeof input === 'string' ? getEncoder().encode(input) : new Uint8Array(input);
  return bytesToBase64(bytes).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const generateTemporaryId = () => {
  const cryptoObj = globalThis.crypto;
  if (cryptoObj?.randomUUID) {
    return cryptoObj.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

async function createZoomJwt(apiKey: string, apiSecret: string): Promise<string> {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) {
    throw new Error('Web Crypto API is not available for Zoom authentication.');
  }

  const header = { alg: 'HS256', typ: 'JWT' };
  const payload = { iss: apiKey, exp: Math.floor(Date.now() / 1000) + 60 };
  const signingInput = `${base64UrlEncode(JSON.stringify(header))}.${base64UrlEncode(JSON.stringify(payload))}`;

  const key = await subtle.importKey(
    'raw',
    getEncoder().encode(apiSecret),
    {
      name: 'HMAC',
      hash: 'SHA-256',
    },
    false,
    ['sign']
  );

  const signature = await subtle.sign('HMAC', key, getEncoder().encode(signingInput));

  return `${signingInput}.${base64UrlEncode(signature)}`;
}

function convertZoomMeetingToEvent(meeting: ZoomMeeting): CalendarEvent | null {
  if (!meeting.start_time || !meeting.join_url) {
    return null;
  }

  const start = new Date(meeting.start_time);
  if (Number.isNaN(start.getTime())) {
    return null;
  }

  const durationMinutes =
    typeof meeting.duration === 'number' && meeting.duration > 0 ? meeting.duration : 30;
  const end = new Date(start.getTime() + durationMinutes * 60 * 1000);

  return {
    id: String(meeting.uuid || meeting.id || generateTemporaryId()),
    summary: meeting.topic || 'Zoom Meeting',
    description: meeting.agenda || '',
    location: meeting.join_url,
    start: {
      dateTime: start.toISOString(),
      timeZone: meeting.timezone,
    },
    end: {
      dateTime: end.toISOString(),
      timeZone: meeting.timezone,
    },
    conferenceData: {
      entryPoints: [
        {
          entryPointType: 'video',
          uri: meeting.join_url,
          label: 'Zoom Meeting',
        },
      ],
    },
    organizer: meeting.host_email
      ? {
          email: meeting.host_email,
          displayName: meeting.host_name || meeting.host_email,
        }
      : undefined,
  };
}

async function fetchZoomMeetings(settings: KioskSettings): Promise<CalendarEvent[]> {
  if (!settings.zoomEnabled) {
    return [];
  }

  if (!settings.zoomApiKey || !settings.zoomApiSecret || !settings.zoomUserId) {
    console.warn('Zoom fetching skipped: missing API credentials or user ID.');
    return [];
  }

  if (typeof window === 'undefined') {
    return [];
  }

  const token = await createZoomJwt(settings.zoomApiKey, settings.zoomApiSecret);
  const response = await fetch(
    `https://api.zoom.us/v2/users/${encodeURIComponent(settings.zoomUserId)}/meetings?type=upcoming`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  const data: ZoomMeetingsResponse = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data.message || 'Failed to fetch Zoom meetings');
  }

  const now = new Date();

  // Get start of tomorrow in user's local timezone
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(0, 0, 0, 0);

  const events = (data.meetings || [])
    .map((meeting) => convertZoomMeetingToEvent(meeting))
    .filter((event): event is CalendarEvent => {
      if (!event) return false;
      const start = new Date(event.start.dateTime);
      return start >= now && start < tomorrow;
    });

  events.sort(
    (a, b) => new Date(a.start.dateTime).getTime() - new Date(b.start.dateTime).getTime()
  );

  return events;
}

export function Dashboard({ onOpenSettings }: DashboardProps) {
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [roomName, setRoomName] = useState('');
  const [showReturnHint, setShowReturnHint] = useState(false);

  const loadEvents = useCallback(async () => {
    const settings = getSettings();

    if (!settings.googleCalendarEnabled || !settings.googleResourceEmail) {
      setError('Please enable and configure at least one calendar source in settings');
      setLoading(false);
      return;
    }

    try {
      setError(null);
      const allEvents: CalendarEvent[] = [];

      // Fetch from Google Calendar if enabled
      if (settings.googleCalendarEnabled && settings.googleResourceEmail) {
        try {
          const googleEvents = await fetchGoogleCalendarEvents(settings.googleResourceEmail);
          allEvents.push(...googleEvents);
        } catch (err) {
          console.error('Error fetching Google Calendar events:', err);
          // Don't throw, continue with other sources
        }
      }

      if (settings.zoomEnabled) {
        try {
          const zoomEvents = await fetchZoomMeetings(settings);
          allEvents.push(...zoomEvents);
        } catch (err) {
          console.error('Error fetching Zoom meetings:', err);
        }
      }
      // TODO: Fetch from Teams if enabled

      setEvents(allEvents);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load events');
      console.error('Error fetching events:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEvents();
    const settings = getSettings();
    setRoomName(settings.roomName || '');

    // Check if user recently left for a meeting
    const returnTime = sessionStorage.getItem('kioskReturnTime');
    if (returnTime) {
      const timeSinceReturn = Date.now() - new Date(returnTime).getTime();
      // Show hint if returned within last 2 minutes
      if (timeSinceReturn < 2 * 60 * 1000) {
        setShowReturnHint(true);
        // Auto-hide after 10 seconds
        setTimeout(() => setShowReturnHint(false), 10000);
      }
      sessionStorage.removeItem('kioskReturnTime');
    }

    const interval = setInterval(loadEvents, settings.refreshInterval * 1000);
    return () => clearInterval(interval);
  }, [loadEvents]);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const handleJoinMeeting = (url: string) => {
    // Store return URL before navigating
    sessionStorage.setItem('kioskReturnTime', new Date().toISOString());
    // Open in same window to stay in kiosk mode
    window.location.href = url;
  };

  const [showPastMeetings, setShowPastMeetings] = useState(false);

  const now = new Date();

  const pastEvents = events
    .filter((event) => {
      const end = new Date(event.end.dateTime);
      return end <= now;
    })
    .slice(-3); // Get last 3 past meetings

  const currentAndFutureEvents = events.filter((event) => {
    const end = new Date(event.end.dateTime);
    return end > now;
  });

  return (
    <div className="min-h-screen bg-background">
      {/* Return to Kiosk Hint */}
      {showReturnHint && (
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50 bg-primary text-primary-foreground px-6 py-3 rounded-lg shadow-lg animate-fade-in flex items-center gap-3">
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 19l-7-7m0 0l7-7m-7 7h18"
            />
          </svg>
          <span className="text-sm font-medium">Press Alt + ← to return to kiosk dashboard</span>
          <button onClick={() => setShowReturnHint(false)} className="ml-2 hover:opacity-70">
            ✕
          </button>
        </div>
      )}

      <header className="flex items-center justify-between px-6 py-3 border-b border-base-300">
        <div className="flex items-center gap-4">
          <LogoIcon className="h-4 w-auto" />
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-content-3">
            {format(currentTime, 'h:mm a  ·  EEE, MMM d')}
          </span>
          {roomName && (
            <span className="text-sm font-medium text-content-1 px-3 py-1 bg-surface-1 rounded-full">
              {roomName}
            </span>
          )}
          <IconButton onClick={onOpenSettings} variant="ghost">
            <Settings className="h-5 w-5 text-content-3" />
          </IconButton>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-12 bg-background">
        {error ? (
          <div className="text-center py-12">
            <div className="inline-block px-6 py-4 rounded-lg mb-6 bg-surface-1 border border-base-300">
              <p className="text-center text-base font-medium text-content-1 mb-2">
                Unable to load meetings
              </p>
              <p className="text-center text-sm text-content-2 mb-4">{error}</p>
              <IconButton onClick={onOpenSettings} variant="primary" className="px-6 py-2 text-sm">
                Configure Settings
              </IconButton>
            </div>
          </div>
        ) : loading ? (
          <div className="text-center py-20">
            <div className="inline-block w-10 h-10 border-4 rounded-full animate-spin mb-4 border-base-300 border-t-primary" />
            <p className="text-base text-content-2">Loading meetings...</p>
          </div>
        ) : currentAndFutureEvents.length === 0 && pastEvents.length === 0 ? (
          <div className="text-center py-20">
            <div className="mb-6">
              <svg
                className="mx-auto mb-4"
                width="120"
                height="120"
                viewBox="0 0 120 120"
                fill="none"
              >
                <circle cx="60" cy="60" r="60" className="fill-surface-1" />
                <path
                  d="M60 30v30l20 20"
                  className="stroke-content-3"
                  strokeWidth="4"
                  strokeLinecap="round"
                />
              </svg>
            </div>
            <h2 className="text-3xl font-normal text-content-1 mb-2 font-serif">
              No upcoming meetings
            </h2>
            <p className="text-base text-content-2">There are no scheduled meetings for today</p>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Past Meetings - Collapsed by default */}
            {pastEvents.length > 0 && (
              <div>
                <button
                  onClick={() => setShowPastMeetings(!showPastMeetings)}
                  className="flex items-center gap-2 mb-4 text-sm text-content-3 hover:opacity-70 transition-opacity cursor-pointer"
                >
                  <svg
                    className="h-4 w-4 transition-transform"
                    style={{
                      transform: showPastMeetings ? 'rotate(90deg)' : 'rotate(0deg)',
                    }}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                  <span>Past meetings ({pastEvents.length})</span>
                </button>
                {showPastMeetings && (
                  <div className="space-y-3 mb-6">
                    {pastEvents.map((event) => (
                      <div key={event.id} className="opacity-60">
                        <MeetingCard event={event} onJoinMeeting={handleJoinMeeting} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Current and Future Meetings */}
            {currentAndFutureEvents.length > 0 && (
              <div>
                <h2 className="text-2xl font-normal text-content-1 mb-6 font-serif">
                  {pastEvents.length > 0 ? 'Upcoming meetings' : "Today's meetings"}
                </h2>
                <div className="space-y-3">
                  {currentAndFutureEvents.map((event) => (
                    <MeetingCard key={event.id} event={event} onJoinMeeting={handleJoinMeeting} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
